/**
 * The shell's one read of the world.
 *
 * Four server facts — who you are, whether your door is up, and every
 * conversation you are in, both directions — assembled into the single list the
 * sidebar draws. Every one of them is an existing query key, so the panels, the
 * rail and the chat pane read exactly what the sidebar read; nothing is fetched
 * twice and nothing can disagree with itself.
 *
 * THE KNOCKS ARRIVE AS THREADS (`docs/conversation-model.md` §6.3):
 * `GET /threads` answers one row per counterparty carrying at most one knock
 * per direction, and the two lists it is zipped from — the inbox and the sent
 * knocks — are no longer read here at all. A row is a person; what they and the
 * reader have said to each other is inside it.
 *
 * The unread marks are folded in here rather than in `lib/conversations.ts` so
 * that the mapper stays a pure function of payloads and can be tested without a
 * browser (`lib/conversations.test.ts`).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Me } from "../../lib/api";
import {
  COMPOSE_ID,
  atOf,
  composeConversation,
  fromHeldSpot,
  fromMyWaiting,
  fromThread,
  ownConversation,
  sidesOf,
  type Conversation,
} from "../../lib/conversations";
import {
  doorQuery,
  doorSettingsQuery,
  doormanThreadQuery,
  heldSpotsQuery,
  meQuery,
  myWaitingQuery,
  onboardingQuery,
  threadsQuery,
} from "../../lib/queries";
import { useSeen } from "../../lib/seen";
import { useShellUi } from "./state";

export const NO_WORD_YET = "Your doorman has not said anything yet.";

export type ShellWorld = {
  me: Me | null;
  /** An X identity that is allowed a door. Everything owner-shaped hangs off it. */
  handle: string | null;
  /** The door exists. `false` only before the email step has hung it (ruling R-1006-2). */
  hasDoor: boolean;
  /**
   * The door step is OWED (PRD §2.3.3, ruling C5) — the server's own answer,
   * not an inference from "no settings came back".
   *
   * The pinned row used to read it off `hasDoor`, which was the same fact by
   * accident: a door existed from the first sign-in, so the only way to be
   * doorless was to be unable to own one. Now a door exists only when the door
   * step completes, and "has not run it yet" is a state with a screen behind it
   * — so the row asks the run rather than inferring the answer.
   */
  owesDoorStep: boolean;
  doorLive: boolean;
  /** A door EXISTS and is paused — distinct from having no door at all. */
  doorPaused: boolean;
  conversations: Conversation[];
  own: Conversation | null;
  byId: (id: string) => Conversation | undefined;
  /**
   * The thread a knock belongs to — for the surfaces that still arrive holding
   * a knock id: the briefing's "open the conversation", a compose thread whose
   * send just came back as a real knock.
   */
  threadOfKnock: (knockId: string) => Conversation | undefined;
};

export function useShellWorld(): ShellWorld {
  const ui = useShellUi();
  const { data: me = null } = useQuery(meQuery());
  const handle = me?.xHandle ?? null;

  // The thread belongs to the PERSON, not to a door they may not have yet
  // (ruling C5), so it is read for any session — a knocker who deferred the
  // door step still has a desk to open.
  const thread = useQuery({ ...doormanThreadQuery(), enabled: Boolean(me) });
  const run = useQuery({ ...onboardingQuery(), enabled: Boolean(me) });
  /**
   * WHETHER THERE IS A DOOR TO ASK ABOUT — the run's own answer, and the gate
   * on asking (gauntlet walk 2026-09-13 round two).
   *
   * This read used to hang off the HANDLE, which every signed-in session has:
   * so a knocker with no door of their own asked for a door's settings on
   * every page of the shell and was told 404, eleven times in one walk, in red,
   * in the console. A 404 is the server answering a question it should never
   * have been asked.
   *
   * `has_door` is the server's own fact (every account has a door from the
   * email step on, ruling R-1006-2; `rules_pending` is now only the tuning
   * step) and it is read strictly: the run has to have ANSWERED
   * before anything is asked, so an unanswered run asks nothing rather than
   * asking on a default. The cost is one round trip on a cold load for an
   * owner, and the run is invalidated by the door step itself (`door-step.tsx`)
   * so the first read after a door is hung is a real one.
   */
  const doorExists = run.data?.onboarding.has_door === true;
  const settings = useQuery({ ...doorSettingsQuery(), enabled: doorExists, retry: false });
  const hasDoor = Boolean(settings.data);

  /**
   * Every conversation, both directions, one row per person (§6.3). Read
   * for any session: a knocker with no door has sent knocks, and those are
   * threads too. The chips narrow what is already here; the server answers one
   * list and this browser slices it.
   */
  const threads = useQuery({ ...threadsQuery(), enabled: Boolean(me), retry: false });

  /**
   * The demand half of the list (WR FR-G9, PRD FR-6.3).
   *
   * Read only for a door owner, because that is the only session these rows
   * belong to: they are people who registered interest at YOUR handle before
   * you had a door. The endpoint answers an empty list for anyone else, so this
   * gate is about not asking rather than about not trusting the answer.
   */
  const held = useQuery({ ...heldSpotsQuery(), enabled: hasDoor, retry: false });

  /**
   * The other half of the demand, and it belongs to ANY session (FR-G3).
   *
   * A spot the reader holds at somebody else's handle is a conversation of
   * theirs — the line they wrote, waiting on a door that does not exist — so it
   * is in the list like everything else they are waiting on. No door gate here,
   * unlike `held` above: a knocker who will never own a door still holds spots,
   * and they are the person the waiting room was built for.
   */
  const waiting = useQuery({ ...myWaitingQuery(), enabled: Boolean(me), retry: false });
  const { unreadFor } = useSeen();

  /**
   * THE ONE ROW THE SERVER HAS NEVER HEARD OF (owner, 2026-08-26).
   *
   * A signed-in knock is written in the console, so while it is being written it
   * has to be IN the list — otherwise opening another conversation to check
   * something is how a half-written knock gets lost. Its whole state is
   * `ui.compose`; the door read here is only for the name on the row, and until
   * it answers the row is addressed by handle, which is what a door is addressed
   * by anyway.
   *
   * ONCE THE SEND LANDS THE ROW IS THE SERVER'S, UNDER THIS ID. The knock exists
   * and the threads read answers with it — as the outgoing side of the thread
   * with that door's owner — so the server's row is what is drawn: its preview,
   * its stage, its timestamp. It keeps `compose` for an id, because the pane
   * holding the hearing is still this thread and a list that pointed somewhere
   * else would leave nothing selected under the conversation on screen. The
   * thread's own id is dropped in the same breath; showing both would be one
   * person in the list twice.
   *
   * AND A DOOR THE READER ALREADY HAS A ROW AT IS THE SAME CASE BEFORE THE SEND
   * (§6.4). There is one knock per sender and door, so opening a compose thread
   * at a door already knocked on opens THAT conversation: the existing thread
   * takes the compose id, the pane draws the hearing rather than a plate and a
   * blank box, and no second knock is ever started.
   *
   * A KNOCK BEGINS AT THE FIRST KEYSTROKE (ruling R-0914-2 (1)). Opening a door
   * is not beginning to write at it: until something has been typed the pane
   * draws the plate, the opening line and the composer exactly as it does now,
   * and the LIST says nothing — no row, and none of the counts that move with
   * one. A list of drafts is then a list of things somebody began to write,
   * rather than a browsing history of every door they looked at. `latent` below
   * is that state: the thread exists, and it is not yet a conversation.
   */
  const compose = ui.compose;
  const composeHandle = compose?.handle ?? null;
  const sentKnockId = compose?.knockId ?? null;
  const composeDoor = useQuery({
    ...doorQuery(composeHandle ?? ""),
    enabled: composeHandle !== null,
    retry: false,
  });

  const threadRows = threads.data;
  const heldSpots = held.data;
  const myWaiting = waiting.data;
  const threadData = thread.data?.ok ? thread.data.thread : null;
  const composeName = composeDoor.data?.display_name ?? null;

  const { conversations, latentCompose } = useMemo<{
    conversations: Conversation[];
    latentCompose: Conversation | null;
  }>(() => {
    const out: Conversation[] = [];
    let latent: Conversation | null = null;
    const rows = (threadRows ?? []).map((t) => fromThread(t, 0));
    /**
     * Which server row, if any, the compose thread IS.
     *
     * Two ways to be that row, and they are OR'd rather than branched on: the
     * knock this console just started, and the knock this reader already has at
     * that door. Under §2 they are the same row — there is one knock per sender
     * and door — so a test of either is a test of it, and asking both means the
     * list can never draw the person twice while one answer is behind the
     * other. Either way the thread wears the compose id and the synthetic draft
     * row is not drawn.
     */
    const composeRow = (row: Conversation): boolean => {
      if (!compose) return false;
      if (sentKnockId !== null && row.outgoing?.knockId === sentKnockId) return true;
      return row.outgoing !== null && row.handle.toLowerCase() === compose.handle.toLowerCase();
    };
    // Drawn until the server's own row can stand in for it — which, after a
    // send, is one round trip away. A row that vanished for that round trip
    // would vanish at the exact moment somebody pressed send.
    const answered = rows.some(composeRow);
    /**
     * THE THREAD BECAME A SPOT (owner, 2026-08-27).
     *
     * The same seam as `sentKnockId` below and for the same reason: a compose
     * thread opened at a handle with no door is where interest gets registered,
     * and the moment it is, the server has a row that says everything this
     * thread now is. So the spot wears the thread's id — the pane holding it
     * does not move, nothing is selected out from under the reader, and the row
     * stops being a draft and starts being a pre-knock. Its own `spot:` copy is
     * dropped in the same breath; showing both would be one spot in the list
     * twice.
     */
    // A spot whose DOOR HAS OPENED no longer claims the compose thread: the
    // registrant pressing "Knock for real" (or just knocking) is converting the
    // spot into a knock, and redrawing their composer as the pre-knock room
    // would be the product arguing with them. The spot row itself still lists
    // below until W10 flips it.
    const composeSpot = compose
      ? (myWaiting ?? []).find(
          (r) => r.handle.toLowerCase() === compose.handle.toLowerCase() && !r.doorOpen,
        )
      : undefined;
    if (compose && !answered) {
      const row = composeSpot
        ? { ...fromMyWaiting(composeSpot, 0), id: COMPOSE_ID }
        : composeConversation({
            handle: compose.handle,
            name: composeName ?? `@${compose.handle}`,
            draft: compose.draft,
            at: compose.at,
            sent: compose.knockId !== null,
          });
      /**
       * WHEN IT BECOMES A ROW (ruling R-0914-2 (1)). Something typed, or a
       * knock already gone in — either is somebody having begun. A held SPOT is
       * listed whatever is typed, because it is not this browser's idea of a
       * draft: it is a line the server holds, and it is drawn here only so that
       * its own `spot:` copy is not drawn twice.
       *
       * Everything else is latent: resolvable by id, so the pane it is open in
       * keeps drawing, and in no list and no count.
       */
      if (composeSpot || compose.draft.trim() !== "" || compose.knockId !== null) out.push(row);
      else latent = row;
    }
    for (const row of rows) {
      // The thread the compose thread IS keeps its id — see above. Everything
      // else about it is the server's.
      const id = composeRow(row) ? COMPOSE_ID : row.id;
      out.push({
        ...row,
        id,
        ...(id !== row.id ? { threadId: row.id } : {}),
        unread: unreadFor(row.id, row.at),
      });
    }
    // Held spots go in the SAME list, which is the whole of FR-6.3: a person
    // looks for "who wanted to reach me" in one place. They are kept apart by
    // stage and by id namespace, never by living somewhere else.
    for (const item of heldSpots ?? []) {
      const row = fromHeldSpot(item, 0);
      out.push({ ...row, unread: unreadFor(row.id, row.at) });
    }
    // And the spots the reader holds elsewhere — the same list again, because
    // "who wanted to reach me" and "who I am waiting on" are both conversations
    // and a person looks for them in one place (FR-6.3).
    for (const item of myWaiting ?? []) {
      if (item === composeSpot) continue; // already drawn, wearing the thread's id
      // A spot whose door has OPENED converts rather than lingers (owner,
      // 2026-08-27): the Nob's notice and the "Knock for real" shelf are its
      // continuation, and a pre-knock row standing beside the knock it should
      // become read as a duplicate. The registration itself stays server-side
      // until W10 flips it on the real knock.
      if (item.doorOpen) continue;
      const row = fromMyWaiting(item, 0);
      out.push({ ...row, unread: unreadFor(row.id, row.at) });
    }
    return { conversations: out, latentCompose: latent };
  }, [compose, composeName, heldSpots, myWaiting, sentKnockId, threadRows, unreadFor]);

  /**
   * The pinned row exists for ANY session, because it is the row that answers
   * "where is my own door" — and "you do not have one yet" is an answer that
   * has to be somewhere. Without it a knocker would have a shell with no way
   * into their own account at all.
   */
  const own = useMemo<Conversation | null>(() => {
    if (!me) return null;
    const last = threadData?.messages.at(-1) ?? null;
    const row = ownConversation({
      handle: handle ?? "",
      name: "Your Nob",
      lastWord: last?.content.trim() || NO_WORD_YET,
      at: atOf(last?.created_at ?? null),
      unread: 0,
    });
    return { ...row, unread: unreadFor(row.id, row.at) };
  }, [handle, me, threadData, unreadFor]);

  const { byId, threadOfKnock } = useMemo(() => {
    const index = new Map<string, Conversation>();
    const byKnock = new Map<string, Conversation>();
    if (own) index.set(own.id, own);
    for (const c of conversations) {
      index.set(c.id, c);
      for (const side of sidesOf(c)) byKnock.set(side.knockId, c);
    }
    // The thread somebody has opened and not yet written in is resolvable but
    // unlisted (ruling R-0914-2 (1)): the chat pane asks this index for what is
    // open, and a door opened to look at still has to draw.
    if (latentCompose) index.set(latentCompose.id, latentCompose);
    return {
      byId: (id: string) => index.get(id),
      threadOfKnock: (knockId: string) => byKnock.get(knockId),
    };
  }, [conversations, latentCompose, own]);

  return {
    me,
    handle,
    hasDoor,
    owesDoorStep: run.data?.onboarding.rules_pending === true,
    doorLive: settings.data?.status === "live",
    doorPaused: settings.data?.status === "paused",
    conversations,
    own,
    byId,
    threadOfKnock,
  };
}
